<template>
  <div class="product-page application-service">
    <!-- 页面标题和描述 -->
    <div class="page-header" :style="{ 
      backgroundImage: pageData.cover ? `url(${pageData.cover})` : '',
    }">
      <div class="container">
        <h1>{{ pageData.name || '应用定制' }}</h1>
        <p class="description">{{ pageData.desc }}</p>
      </div>
    </div>

    <!-- 服务内容 -->
    <section class="section services-section">
      <div class="container">
        <h2 class="section-title">服务内容</h2>
        <div class="grid-layout col-3">
          <div 
            v-for="(service, index) in services" 
            :key="service.title"
            class="card service-card"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="icon-wrapper">
              <el-icon :size="32">
                <component :is="service.icon" />
              </el-icon>
            </div>
            <h3>{{ service.title }}</h3>
            <p>{{ service.description }}</p>
            <ul class="feature-list">
              <li v-for="feature in service.features" :key="feature">{{ feature }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- 服务优势 -->
    <section class="section advantages-section">
      <div class="container">
        <h2 class="section-title">服务优势</h2>
        <div class="grid-layout col-4">
          <div 
            v-for="(advantage, index) in advantages" 
            :key="advantage.title"
            class="card advantage-card"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="icon-wrapper">
              <el-icon :size="32">
                <component :is="advantage.icon" />
              </el-icon>
            </div>
            <h3>{{ advantage.title }}</h3>
            <p>{{ advantage.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 开发流程 -->
    <section class="section process-section">
      <div class="container">
        <h2 class="section-title">开发流程</h2>
        <div class="process-container">
          <div 
            v-for="(step, index) in process" 
            :key="step.title"
            class="process-card"
            :class="{ active: activeStep === index }"
            @mouseenter="activeStep = index"
          >
            <div class="process-icon">
              <div class="step-number">{{ index + 1 }}</div>
              <div class="connection-line" v-if="index < process.length - 1"></div>
            </div>
            <div class="process-content">
              <h3>{{ step.title }}</h3>
              <p>{{ step.description }}</p>
              <div class="process-points">
                <div 
                  v-for="point in step.points" 
                  :key="point"
                  class="point-item"
                >
                  <el-icon><CircleCheck /></el-icon>
                  <span>{{ point }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 应用案例 -->
    <!-- <section class="section cases-section">
      <div class="container">
        <h2 class="section-title">应用案例</h2>
        <div class="cases-showcase">
          <div class="cases-container">
            <div 
              v-for="(item, index) in productData.cases" 
              :key="item.title"
              class="case-item"
              :class="{ active: activeCase === index }"
              @click="handleCaseClick(index)"
            >
              <div class="case-preview">
                <img src="@/assets/images/2.png" :alt="item.title">
                <div class="case-number">0{{ index + 1 }}</div>
              </div>
              <div class="case-details">
                <div class="case-header">
                  <h3>{{ item.title }}</h3>
                  <div class="tags-wrapper">
                    <span v-for="tag in item.tags" :key="tag" class="tag">{{ tag }}</span>
                  </div>
                </div>
                <div class="case-body">
                  <p class="description">{{ item.description }}</p>
                  <div class="highlights">
                    <div 
                      v-for="highlight in item.highlights" 
                      :key="highlight"
                      class="highlight-item"
                    >
                      <el-icon><CircleCheck /></el-icon>
                      <span>{{ highlight }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section> -->
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { columnGet2 } from "@/api/court";
import { 
  Monitor, 
  Setting, 
  Tools, 
  Lock,
  Platform, 
  Management, 
  Connection, 
  DataLine,
  CircleCheck
} from '@element-plus/icons-vue'
import axios from 'axios'
import { getBaseApiUrl } from '@/utils/api-config'

const baseUrl = getBaseApiUrl()
const pageData = ref({})

const activeStep = ref(0)
const activeCase = ref(0)

// 获取页面数据
const getPageData = async () => {
  try {
    const response = await columnGet2({
      page: 1,
      limit: 10000,
      is_enable: true,
      parent: 9,
    });
    console.log(response);
    if (response.data.code === 2000) {
      // 根据当前页面名称匹配对应的数据
      const data = response.data.data.data.find(item => {
        // 根据路径匹配对应的数据
        return item.name.includes('应用定制') || item.name.toLowerCase().includes('application')
      })
      if (data) {
        pageData.value = data
        console.log(data.cover);
      }
    }
  } catch (error) {
    console.error('获取页面数据失败:', error)
  }
}

onMounted(() => {
  getPageData()
})

// 服务内容
const services = [
  {
    icon: Platform,
    title: '应用开发',
    description: '基于低代码平台的快速应用开发',
    features: [
      '业务系统定制开发',
      '系统重构与升级',
      '数据集成与对接',
      '性能优化与调优'
    ]
  },
  {
    icon: Connection,
    title: '网站开发',
    description: '响应式企业网站开发服务',
    features: [
      '企业官网建设',
      '电子商务平台',
      'CMS内容管理',
      'SEO优化支持'
    ]
  },
  {
    icon: Management,
    title: '小程序开发',
    description: '专业的小程序定制开发',
    features: [
      '微信小程序开发',
      '企业微信应用',
      '跨平台应用开发',
      '数据分析集成'
    ]
  }
]

// 服务优势
const advantages = [
  {
    icon: Tools,
    title: '经验和专业技能',
    description: '丰富的项目经验和专业的技术团队'
  },
  {
    icon: Lock,
    title: '成功案例',
    description: '众多成功案例和客户好评'
  },
  {
    icon: Setting,
    title: '沟通能力',
    description: '高效的沟通和快速的响应'
  },
  {
    icon: Monitor,
    title: '售后服务',
    description: '完善的售后支持和运维服务'
  }
]

// 开发流程
const process = [
  {
    title: '需求分析',
    description: '深入了解客户需求，制定开发方案',
    points: [
      '业务需求调研',
      '技术方案设计',
      '项目计划制定'
    ]
  },
  {
    title: '开发实施',
    description: '专业团队开发，确保项目质量',
    points: [
      '敏捷开发流程',
      '质量控制管理',
      '进度监控把控'
    ]
  },
  {
    title: '测试验收',
    description: '全面测试，确保系统稳定',
    points: [
      '功能测试验证',
      '性能测试优化',
      '系统部署上线'
    ]
  },
  {
    title: '运维支持',
    description: '持续的技术服务支持',
    points: [
      '系统运维保障',
      '技术支持服务',
      '持续优化升级'
    ]
  }
]

// 获取图片URL的方法
const getImageUrl = (index) => {
  try {
    return require(`@/assets/images/case${index}.jpg`)
  } catch (error) {
    return require('@/assets/images/15.png')
  }
}

// 更新案例数据
const productData = {
  title: '应用定制',
  description: '随着大数据、云计算等技术的不断发展，业务应用系统的开发重点从底层逻辑技术实现逐步向业务流程开发与数据资源整合为重点。湖南美音基于多年智慧城市、行业大数据项目的建设经验，逐步形成以低代码开发平台为主要手段，基于大数据使用角度为客户提供业务系统重构、规划与定制开发，助力客户数字化转型。',
  partners: [
    { name: '阿里云', logo: '/src/assets/images/3.png' },
    { name: '腾讯云', logo: '/src/assets/images/3.png' },
    { name: '华为云', logo: '/src/assets/images/3.png' },
    { name: '金蝶', logo: '/src/assets/images/3.png' }
  ],
  cases: [
    {
      title: '智慧政务服务平台',
      description: '为政府部门打造一站式政务服务平台，整合各类政务服务资源，提升政务服务效率和质量，实现"一网通办"。',
      tags: ['政务服务', '流程管理', '数据共享'],
      highlights: [
        '服务集成化',
        '流程简单化',
        '数据共享化',
        '监管智能化'
      ]
    },
    {
      title: '企业数字化管理系统',
      description: '面向企业的综合业务管理系统，涵盖市场营销、生产经营、人事管理、采购、财务等核心业务，助力企业数字化转型。',
      tags: ['企业管理', '流程优化', '数字化转型'],
      highlights: [
        '流程标准化',
        '数据一体化',
        '管理精细化',
        '决策智能化'
      ]
    },
    {
      title: '智慧园区管理平台',
      description: '为产业园区提供智能化管理平台，实现园区安防、能耗、物业、企业服务等全方位管理，打造智慧园区标杆。',
      tags: ['园区管理', '智能运营', '综合服务'],
      highlights: [
        '管理智能化',
        '服务便捷化',
        '运营高效化',
        '安防立体化'
      ]
    }
  ]
}

// 处理案例点击
const handleCaseClick = (index) => {
  activeCase.value = index
}

// 自动切换案例
let autoplayInterval = null

onMounted(() => {
  startAutoplay()
})

onUnmounted(() => {
  stopAutoplay()
})

const startAutoplay = () => {
  autoplayInterval = setInterval(() => {
    activeCase.value = (activeCase.value + 1) % productData.cases.length
  }, 5000)
}

const stopAutoplay = () => {
  if (autoplayInterval) {
    clearInterval(autoplayInterval)
    autoplayInterval = null
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/products.scss';

.application-service {
  padding-top: 30px;

  .process-section {
    background: linear-gradient(135deg, #fff 0%, #f8f9fc 100%);
    padding: 80px 0;
    position: relative;
    overflow: hidden;

    // &::before {
    //   content: '';
    //   position: absolute;
    //   width: 600px;
    //   height: 600px;
    //   background:rgba(0, 0, 0, 0.5);
    //   bottom: -300px;
    //   right: -300px;
    //   border-radius: 50%;
    // }
  }

  .process-container {
    max-width: 1000px;
    margin: 60px auto 0;
    padding: 0 20px;
    position: relative;
  }

  .process-card {
    display: flex;
    gap: 30px;
    padding: 30px 0;
    position: relative;
    transition: all 0.4s ease;

    &:hover, &.active {
      .process-icon {
        .step-number {
          background: #2b5dff;
          color: #fff;
          transform: scale(1.1);
        }

        .connection-line {
          &::before {
            height: 100%;
          }
        }
      }

      .process-content {
        transform: translateX(10px);

        .point-item {
          background: rgba(43, 93, 255, 0.08);
          color: #2b5dff;
        }
      }
    }

    .process-icon {
      position: relative;
      z-index: 1;

      .step-number {
        width: 50px;
        height: 50px;
        background: rgba(43, 93, 255, 0.1);
        border-radius: 25px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        font-weight: 600;
        color: #2b5dff;
        transition: all 0.4s ease;
      }

      .connection-line {
        position: absolute;
        top: 50px;
        left: 24px;
        bottom: -30px;
        width: 2px;
        background: rgba(43, 93, 255, 0.1);
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 0;
          background: #2b5dff;
          transition: height 0.6s ease;
        }
      }
    }

    .process-content {
      flex: 1;
      transition: all 0.4s ease;
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(43, 93, 255, 0.1);
      border-radius: 20px;
      padding: 25px;

      h3 {
        font-size: 22px;
        color: #333;
        margin-bottom: 12px;
        display: flex;
        align-items: center;
        gap: 10px;
      }

      p {
        color: #666;
        line-height: 1.6;
        margin-bottom: 20px;
      }

      .process-points {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 12px;

        .point-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 15px;
          background: rgba(255, 255, 255, 0.7);
          border-radius: 10px;
          font-size: 14px;
          color: #666;
          transition: all 0.3s ease;

          .el-icon {
            color: #2b5dff;
            font-size: 16px;
          }

          &:hover {
            transform: translateX(5px);
          }
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    .process-card {
      gap: 20px;

      .process-content {
        padding: 20px;

        h3 {
          font-size: 20px;
        }

        .process-points {
          grid-template-columns: 1fr;
          gap: 10px;

          .point-item {
            padding: 8px 12px;
          }
        }
      }
    }
  }

  .cases-section {
    background: linear-gradient(135deg, #f8f9fc 0%, #fff 100%);
    padding: 80px 0;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      width: 600px;
      height: 600px;
      background: radial-gradient(circle, rgba(43, 93, 255, 0.03) 0%, transparent 70%);
      top: -300px;
      left: -300px;
      border-radius: 50%;
    }
  }

  .cases-showcase {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 40px;
  }

  .cases-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
    padding: 40px 0;
  }

  .case-item {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(43, 93, 255, 0.1);
    border-radius: 20px;
    overflow: hidden;
    transition: all 0.4s ease;
    cursor: pointer;
    position: relative;

    &:hover, &.active {
      transform: translateY(-10px);
      border-color: rgba(43, 93, 255, 0.3);
      box-shadow: 0 20px 40px rgba(43, 93, 255, 0.1);

      .case-preview {
        img {
          transform: scale(1.1);
        }

        .case-number {
          transform: translateY(-5px);
          background: #2b5dff;
          color: #fff;
        }
      }

      .highlight-item {
        background: rgba(43, 93, 255, 0.08);
        color: #2b5dff;
      }
    }

    .case-preview {
      position: relative;
      height: 200px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.6s ease;
      }

      .case-number {
        position: absolute;
        top: 20px;
        right: 20px;
        width: 40px;
        height: 40px;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: 600;
        color: #2b5dff;
        transition: all 0.4s ease;
      }
    }

    .case-details {
      padding: 25px;
    }

    .case-header {
      margin-bottom: 20px;

      h3 {
        font-size: 20px;
        color: #333;
        margin-bottom: 12px;
      }

      .tags-wrapper {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;

        .tag {
          padding: 4px 12px;
          background: rgba(43, 93, 255, 0.08);
          border-radius: 20px;
          font-size: 13px;
          color: #2b5dff;
          font-weight: 500;
        }
      }
    }

    .case-body {
      .description {
        font-size: 14px;
        line-height: 1.6;
        color: #666;
        margin-bottom: 20px;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .highlights {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;

        .highlight-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.7);
          border-radius: 8px;
          font-size: 13px;
          color: #666;
          transition: all 0.3s ease;

          .el-icon {
            color: #2b5dff;
            font-size: 16px;
          }
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    .cases-container {
      grid-template-columns: 1fr;
      gap: 30px;
    }
  }

  @media screen and (max-width: 768px) {
    .cases-showcase {
      padding: 0 20px;
    }

    .case-item {
      .case-preview {
        height: 180px;
      }

      .case-details {
        padding: 20px;
      }

      .case-body {
        .highlights {
          grid-template-columns: 1fr;
        }
      }
    }
  }
}

.page-header {
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 19px,
      rgba(255,255,255,0.1) 19px,
      rgba(255,255,255,0.1) 20px
    );
    opacity: 0.1;
  }
}
</style> 